import { createContext, useContext, useMemo, useRef } from "react"

import { Box } from "js-quadtree"
import { QuadTreeConfig } from "js-quadtree/dist/types"

import { useTooltipContext } from "./TooltipProvider"
import {
  initialState,
  initialTooltipDataState,
  qtDefaultConfig
} from "./config"
import { Qtree } from "./Qtree"

type TooltipDataContext = Omit<Qtree, "query">

interface TooltipDataProviderProps {
  children: React.ReactNode
  canvasWidth: number
  canvasHeight: number
  x?: number
  y?: number
  qtConfig?: QuadTreeConfig
  clearOnPropsChange?: boolean
}

function boundingArea(x = 0, y = 0, width = 0, height = 0) {
  return new Box(x, y, width, height)
}

function calcPostion(x, y, width, height) {
  const w2 = width / 2
  const gap = 0

  const isOnTheLeftArea = x < w2

  return {
    coords: {
      x: isOnTheLeftArea ? 0 : x,
      y: y - 12
    },
    containerSize: {
      widthStartsAt: isOnTheLeftArea ? x + gap : 0,
      width: isOnTheLeftArea ? width - x - gap : width - (width - x) - gap,
      height: height + 12
    }
  }
}

// Context
export const TooltipDataContext = createContext<TooltipDataContext>(
  initialTooltipDataState
)

// Hook
export function useTooltipDataContext(): TooltipDataContext {
  return useContext<TooltipDataContext>(TooltipDataContext)
}

// Provider
export default function TooltipDataProvider({
  children,
  x,
  y,
  canvasWidth,
  canvasHeight,
  qtConfig,
  clearOnPropsChange = true
}: TooltipDataProviderProps) {
  const { setToolTipData } = useTooltipContext()

  const qtRef = useRef<Omit<Qtree, "query">>(
    new Qtree(
      boundingArea(x, y, canvasWidth, canvasHeight),
      qtConfig || qtDefaultConfig,
      (points) =>
        setToolTipData({
          ...initialState,
          data: points
        })
    )
  )

  qtRef.current = useMemo(() => {
    // On props change clear the data before creating a new instance
    if (qtRef.current && clearOnPropsChange) {
      qtRef.current.clear()
    }

    const callback = (points, x = 0, y = 0) =>
      setToolTipData({
        data: points,
        ...calcPostion(x, y, canvasWidth, canvasHeight)
      })

    return new Qtree(
      boundingArea(x, y, canvasWidth, canvasHeight),
      {
        ...qtDefaultConfig,
        ...qtConfig
      },
      callback
    )
  }, [x, y, canvasWidth, canvasHeight, qtConfig])

  return (
    <TooltipDataContext.Provider value={qtRef.current}>
      {children}
    </TooltipDataContext.Provider>
  )
}
